import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';


import './FabricSpecificationPage.css';

const SELL_PRICE_RECENT_MS = 14 * 24 * 60 * 60 * 1000; // highlight window for recently-updated prices

const initialFormState = {
  type: '',
  quality: '',
  fabricNameForOffice: '',
  fabric: '',
  skuPrefix: '',
  skuSubPrefix: '',
  skuSuffix: '',
  sellPrice: '',
  purchasePrice: '',
  width: '',
  weightMode: 'fixed',
  weightInGrams: '',
  gsm: '',
  batchQuality: 'Polyester', // Default value
  batchPrefix: 'POLY', // Derived from default batchQuality
  printQualityEnabled: false,
  printQualityDisplayName: '',
};

// "43 Inch" out of "110 CM | 43 Inch" — used only to build a suggested Print Quality Display Name
// when two rows share a `fabric` name (e.g. two widths of the same fabric both checked in), never
// to compute anything that gets saved without the admin seeing/editing it first.
function extractInches(width) {
  const match = String(width || '').match(/(\d+(?:\.\d+)?)\s*Inch/i);
  return match ? `${match[1]}"` : '';
}

function FabricSpecificationPage() {
  const { currentUser, canPerformAction } = useAuth();
  const [specs, setSpecs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [formState, setFormState] = useState(initialFormState);
  
  const [editingId, setEditingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rowWeightDrafts, setRowWeightDrafts] = useState({});
  const [rowWeightSavingId, setRowWeightSavingId] = useState('');
  const [rowGsmDrafts, setRowGsmDrafts] = useState({});
  const [editingGsmId, setEditingGsmId] = useState('');
  const [rowGsmSavingId, setRowGsmSavingId] = useState('');
  const [rowSellPriceDrafts, setRowSellPriceDrafts] = useState({});
  const [editingSellPriceId, setEditingSellPriceId] = useState('');
  const [rowSellPriceSavingId, setRowSellPriceSavingId] = useState('');
  const [rowPrintQualityNameDrafts, setRowPrintQualityNameDrafts] = useState({});
  const [editingPrintQualityNameId, setEditingPrintQualityNameId] = useState('');
  const [rowPrintQualitySavingId, setRowPrintQualitySavingId] = useState('');
  const [tableFilters, setTableFilters] = useState({
    type: '',
    quality: '',
    skuPrefix: '',
    skuSubPrefix: '',
    skuSuffix: '',
    fabricNameForOffice: '',
    fabric: '',
  });
  const [sortConfig, setSortConfig] = useState({ key: 'type', direction: 'asc' });
  const [discountPct, setDiscountPct] = useState('');
  const [missingSpecs, setMissingSpecs] = useState([]);
  const [isFetchingMissing, setIsFetchingMissing] = useState(false);

  const [syncStatus, setSyncStatus] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  const uniqueValues = useMemo(() => {
    const values = {
      type: new Set(),
      quality: new Set(),
      fabricNameForOffice: new Set(),
      fabric: new Set(),
      width: new Set(),
    };
    specs.forEach(spec => {
      if (spec.type) values.type.add(spec.type);
      if (spec.quality) values.quality.add(spec.quality);
      if (spec.fabricNameForOffice) values.fabricNameForOffice.add(spec.fabricNameForOffice);
      if (spec.fabric) values.fabric.add(spec.fabric);
      if (spec.width) values.width.add(spec.width);
    });
    return {
      type: [...values.type].sort(),
      quality: [...values.quality].sort(),
      fabricNameForOffice: [...values.fabricNameForOffice].sort(),
      fabric: [...values.fabric].sort(),
      width: [...values.width].sort(),
    };
  }, [specs]);

  const displayedSpecs = useMemo(() => {
    const includesText = (value, query) => String(value || '').toLowerCase().includes(String(query || '').trim().toLowerCase());
    const filtered = specs.filter((spec) => (
      includesText(spec.type, tableFilters.type) &&
      includesText(spec.quality, tableFilters.quality) &&
      includesText(spec.skuPrefix, tableFilters.skuPrefix) &&
      includesText(spec.skuSubPrefix, tableFilters.skuSubPrefix) &&
      includesText(spec.skuSuffix, tableFilters.skuSuffix) &&
      includesText(spec.fabricNameForOffice, tableFilters.fabricNameForOffice) &&
      includesText(spec.fabric, tableFilters.fabric)
    ));

    const dir = sortConfig.direction === 'desc' ? -1 : 1;
    const key = String(sortConfig.key || '').trim();
    const sorted = [...filtered].sort((a, b) => {
      const left = String(a?.[key] || '').toLowerCase();
      const right = String(b?.[key] || '').toLowerCase();
      if (left < right) return -1 * dir;
      if (left > right) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [specs, tableFilters, sortConfig]);

  const computeSuggestedPrice = useCallback((sellPrice) => {
    const pct = Number(String(discountPct).trim());
    if (!Number.isFinite(pct) || pct <= 0 || pct >= 100) return '';
    const price = Number(sellPrice);
    if (!Number.isFinite(price) || price <= 0) return '';
    return Math.ceil(price / (1 - pct / 100));
  }, [discountPct]);

  const isSellPriceRecent = useCallback((spec) => {
    const ms = Number(spec?.sellPriceUpdatedAtMs);
    return Number.isFinite(ms) && ms > 0 && (Date.now() - ms) < SELL_PRICE_RECENT_MS;
  }, []);

  const formatSellPriceDate = (ms) => {
    const n = Number(ms);
    if (!Number.isFinite(n) || n <= 0) return '';
    return new Date(n).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const sellPriceDaysAgo = (spec) => {
    const ms = Number(spec?.sellPriceUpdatedAtMs);
    if (!Number.isFinite(ms) || ms <= 0) return '';
    const days = Math.floor((Date.now() - ms) / (24 * 60 * 60 * 1000));
    return days <= 0 ? 'today' : `${days}d ago`;
  };

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const fetchSpecs = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/fabric-specifications', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch fabric specifications.');
      const data = await response.json();
      setSpecs(data.specifications || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchSpecs();
  }, [fetchSpecs]);

  const fetchMissingSpecs = useCallback(async () => {
    if (!canPerformAction('fabricSpecs.read')) return; // Assuming a read permission might exist
    setIsFetchingMissing(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/fabric-specifications/missing', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch missing specifications.');
      const data = await response.json();
      setMissingSpecs(data.missingSpecSkus);
    } catch (err) {
      setError(prev => `${prev ? prev + ' ' : ''}${err.message}`); // Append to existing errors
    } finally {
      setIsFetchingMissing(false);
    }
  }, [currentUser, canPerformAction]);

  useEffect(() => {
    fetchMissingSpecs();
  }, [fetchMissingSpecs]);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/admin/print-qualities/status', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.success) setSyncStatus(data.status);
    } catch {
      // Non-critical — status display just stays blank if this fails.
    }
  }, [currentUser]);

  useEffect(() => {
    fetchSyncStatus();
  }, [fetchSyncStatus]);

  const handleSyncPrintQualities = async () => {
    setIsSyncing(true);
    setSyncMessage('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/admin/print-qualities/sync', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Sync failed.');
      setSyncMessage(
        data.summary.skipped
          ? data.summary.reason
          : `Synced ${data.summary.qualitiesCount} print qualities to "${data.summary.productHandle}".`
      );
      await fetchSyncStatus();
    } catch (err) {
      setSyncMessage(`Error: ${err.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setFormState(prev => {
      const newState = { ...prev, [name]: nextValue };
      // Automatically update batchPrefix when batchQuality changes
      if (name === 'batchQuality') {
        if (value === 'Polyester') newState.batchPrefix = 'POLY';
        else if (value === 'Pure') newState.batchPrefix = 'PURE';
        else if (value === 'Plain') newState.batchPrefix = 'PLAIN';
        else if (value === 'Modal') newState.batchPrefix = 'MODAL';
        else if (value === 'Cotton') newState.batchPrefix = 'COTTON';
      }
      // Checking a row into Print Qualities while another row for the same fabric is already
      // checked in (and this row's display name is still blank) needs a distinguishing name, or
      // the backend will reject it as a duplicate — pre-fill a suggestion built from the fabric
      // name + width, fully editable, never silently saved as-is.
      if (name === 'printQualityEnabled' && checked && !prev.printQualityDisplayName) {
        const otherEnabled = specs.find(
          (spec) => spec.id !== editingId && spec.printQualityEnabled && spec.fabric === prev.fabric,
        );
        if (otherEnabled) {
          const inches = extractInches(prev.width);
          newState.printQualityDisplayName = inches ? `${prev.fabric} — ${inches} Width` : prev.fabric;
        }
      }
      return newState;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const url = editingId ? `/api/v1/fabric-specifications/${editingId}` : '/api/v1/fabric-specifications';
    const method = editingId ? 'PUT' : 'POST';
    const payload = {
      ...formState,
      // Dynamic specs intentionally keep grams empty.
      weightInGrams: String(formState.weightMode || 'fixed').toLowerCase() === 'dynamic'
        ? ''
        : formState.weightInGrams,
    };

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || `Failed to ${editingId ? 'update' : 'create'} specification.`);
      
      alert(`Specification ${editingId ? 'updated' : 'created'} successfully!`);
      handleCancelEdit();
      fetchSpecs();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (spec) => {
    setEditingId(spec.id);
    setFormState({
      type: spec.type || '',
      quality: spec.quality || '',
      fabricNameForOffice: spec.fabricNameForOffice || '',
      fabric: spec.fabric || '',
      skuPrefix: spec.skuPrefix || '',
      skuSubPrefix: spec.skuSubPrefix || '',
      skuSuffix: spec.skuSuffix || '',
      sellPrice: spec.sellPrice || '',
      purchasePrice: spec.purchasePrice || '',
      width: spec.width || '',
      weightMode: (spec.weightMode || 'fixed').toLowerCase() === 'dynamic' ? 'dynamic' : 'fixed',
      weightInGrams: spec.weightInGrams == null ? '' : String(spec.weightInGrams),
      gsm: spec.gsm == null ? '' : String(spec.gsm),
      batchQuality: spec.batchQuality || 'Polyester',
      batchPrefix: spec.batchPrefix || 'POLY',
      printQualityEnabled: spec.printQualityEnabled === true,
      printQualityDisplayName: spec.printQualityDisplayName || '',
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this specification?')) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/fabric-specifications/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete specification.');
      alert('Specification deleted successfully!');
      fetchSpecs();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormState(initialFormState);
  };

  const buildSpecUpdatePayload = (spec, overrides = {}) => ({
    type: spec.type || '',
    quality: spec.quality || '',
    fabricNameForOffice: spec.fabricNameForOffice || '',
    fabric: spec.fabric || '',
    skuPrefix: spec.skuPrefix || '',
    skuSubPrefix: spec.skuSubPrefix || '',
    skuSuffix: spec.skuSuffix || '',
    sellPrice: spec.sellPrice || '',
    purchasePrice: spec.purchasePrice || '',
    width: spec.width || '',
    batchQuality: spec.batchQuality || 'Polyester',
    batchPrefix: spec.batchPrefix || (spec.batchQuality === 'Pure' ? 'PURE' : 'POLY'),
    weightMode: (spec.weightMode || 'fixed').toLowerCase() === 'dynamic' ? 'dynamic' : 'fixed',
    weightInGrams: spec.weightInGrams == null ? '' : String(spec.weightInGrams),
    gsm: spec.gsm == null ? '' : String(spec.gsm),
    // Every inline-edit path (sell price, weight, charm pricing) round-trips through this
    // builder — without carrying these two fields forward, an unrelated inline edit would
    // silently reset an already-checked row's Print Quality status back off.
    printQualityEnabled: spec.printQualityEnabled === true,
    printQualityDisplayName: spec.printQualityDisplayName || '',
    ...overrides,
  });

  const handleInlineWeightSave = async (spec) => {
    const id = String(spec?.id || '').trim();
    if (!id) return;
    const draft = String(rowWeightDrafts[id] ?? '').trim();
    const grams = Number(draft);
    if (!Number.isFinite(grams) || grams <= 0) {
      setError('Please enter valid weight in grams (> 0).');
      return;
    }
    try {
      setRowWeightSavingId(id);
      setError('');
      const token = await currentUser.getIdToken();
      const payload = buildSpecUpdatePayload(spec, {
        weightMode: 'fixed',
        weightInGrams: String(Math.round(grams)),
      });
      const response = await fetch(`/api/v1/fabric-specifications/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save weight.');
      setRowWeightDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      await fetchSpecs();
    } catch (err) {
      setError(err.message || 'Failed to save weight.');
    } finally {
      setRowWeightSavingId('');
    }
  };

  const saveGsm = async (spec, rawValue) => {
    const id = String(spec?.id || '').trim();
    if (!id) return;
    const gsm = Number(String(rawValue ?? '').trim());
    if (!Number.isFinite(gsm) || gsm <= 0) {
      setError('Please enter a valid GSM (> 0).');
      return;
    }
    try {
      setRowGsmSavingId(id);
      setError('');
      const token = await currentUser.getIdToken();
      const payload = buildSpecUpdatePayload(spec, { gsm: String(Math.round(gsm)) });
      const response = await fetch(`/api/v1/fabric-specifications/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save GSM.');
      setEditingGsmId('');
      setRowGsmDrafts((prev) => { const next = { ...prev }; delete next[id]; return next; });
      await fetchSpecs();
    } catch (err) {
      setError(err.message || 'Failed to save GSM.');
    } finally {
      setRowGsmSavingId('');
    }
  };

  const handleGsmEditStart = (spec) => {
    const id = String(spec?.id || '');
    setEditingGsmId(id);
    setRowGsmDrafts((prev) => ({ ...prev, [id]: spec.gsm == null ? '' : String(spec.gsm) }));
  };

  const handleGsmEditCancel = (id) => {
    setEditingGsmId('');
    setRowGsmDrafts((prev) => { const next = { ...prev }; delete next[id]; return next; });
  };

  const saveSellPrice = async (spec, rawValue) => {
    const id = String(spec?.id || '').trim();
    if (!id) return;
    const price = Number(String(rawValue ?? '').trim());
    if (!Number.isFinite(price) || price <= 0) {
      setError('Please enter a valid sell price (> 0).');
      return;
    }
    try {
      setRowSellPriceSavingId(id);
      setError('');
      const token = await currentUser.getIdToken();
      const payload = buildSpecUpdatePayload(spec, { sellPrice: String(Math.round(price)) });
      const response = await fetch(`/api/v1/fabric-specifications/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save sell price.');
      setEditingSellPriceId('');
      setRowSellPriceDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      await fetchSpecs();
    } catch (err) {
      setError(err.message || 'Failed to save sell price.');
    } finally {
      setRowSellPriceSavingId('');
    }
  };

  const handleSellPriceEditStart = (spec) => {
    const id = String(spec?.id || '');
    setEditingSellPriceId(id);
    setRowSellPriceDrafts((prev) => ({ ...prev, [id]: spec.sellPrice == null ? '' : String(spec.sellPrice) }));
  };

  const handleSellPriceEditCancel = (id) => {
    setEditingSellPriceId('');
    setRowSellPriceDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // Shared save path for both the inline checkbox toggle and the inline display-name edit —
  // both just PUT the full spec back through buildSpecUpdatePayload with an override, same as
  // every other inline edit on this page (sell price, weight). Returns whether it succeeded so
  // callers can decide whether to close out their own local editing state.
  const savePrintQualityFields = async (spec, overrides) => {
    const id = String(spec?.id || '').trim();
    if (!id) return false;
    try {
      setRowPrintQualitySavingId(id);
      setError('');
      const token = await currentUser.getIdToken();
      const payload = buildSpecUpdatePayload(spec, overrides);
      const response = await fetch(`/api/v1/fabric-specifications/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save Print Quality.');
      await fetchSpecs();
      return true;
    } catch (err) {
      setError(err.message || 'Failed to save Print Quality.');
      return false;
    } finally {
      setRowPrintQualitySavingId('');
    }
  };

  const handleTogglePrintQuality = (spec) => {
    const turningOn = !spec.printQualityEnabled;
    let displayNameOverride = spec.printQualityDisplayName || '';
    // Same auto-suggest as the add/edit form: turning this on while another row for the same
    // fabric is already checked in needs a distinguishing name, or the backend will reject it as
    // a duplicate effective name.
    if (turningOn && !displayNameOverride) {
      const otherEnabled = specs.find(
        (s) => s.id !== spec.id && s.printQualityEnabled && s.fabric === spec.fabric,
      );
      if (otherEnabled) {
        const inches = extractInches(spec.width);
        displayNameOverride = inches ? `${spec.fabric} — ${inches} Width` : spec.fabric;
      }
    }
    savePrintQualityFields(spec, {
      printQualityEnabled: turningOn,
      printQualityDisplayName: displayNameOverride,
    });
  };

  const handlePrintQualityNameEditStart = (spec) => {
    const id = String(spec?.id || '');
    setEditingPrintQualityNameId(id);
    setRowPrintQualityNameDrafts((prev) => ({ ...prev, [id]: spec.printQualityDisplayName || '' }));
  };

  const handlePrintQualityNameEditCancel = (id) => {
    setEditingPrintQualityNameId('');
    setRowPrintQualityNameDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const savePrintQualityName = async (spec, rawValue) => {
    const id = String(spec?.id || '').trim();
    if (!id) return;
    const ok = await savePrintQualityFields(spec, { printQualityDisplayName: String(rawValue || '').trim() });
    if (ok) handlePrintQualityNameEditCancel(id);
  };

  const charmNearestEnding = (value) => {
    const v = Number(value);
    if (!Number.isFinite(v) || v <= 0) return '';
    const x = (v - 9) / 10;
    const k = Math.round(x);
    return (k * 10) + 9;
  };

  const handleApplySuggested = (spec) => {
    const suggested = computeSuggestedPrice(spec.sellPrice);
    if (suggested === '') return;
    if (!window.confirm(`Set sell price to ${suggested} (from ${spec.sellPrice})?`)) return;
    saveSellPrice(spec, suggested);
  };

  const handleApplySuggestedCharm = (spec) => {
    const suggested = computeSuggestedPrice(spec.sellPrice);
    const targetPrice = suggested !== '' ? suggested : spec.sellPrice;
    if (!targetPrice) return;
    const charmed = charmNearestEnding(targetPrice);
    if (!charmed) return;
    if (!window.confirm(`Set sell price to charmed price ${charmed} (from ${spec.sellPrice})?`)) return;
    saveSellPrice(spec, String(charmed));
  };

  const handleApplyAllSuggestedCharm = async () => {
    const specsToUpdate = [];
    displayedSpecs.forEach((spec) => {
      const suggested = computeSuggestedPrice(spec.sellPrice);
      const targetPrice = suggested !== '' ? suggested : spec.sellPrice;
      if (!targetPrice) return;
      const charmed = charmNearestEnding(targetPrice);
      if (charmed && Math.abs(Number(charmed) - Number(spec.sellPrice)) >= 0.01) {
        specsToUpdate.push({ spec, charmedPrice: charmed });
      }
    });

    if (specsToUpdate.length === 0) {
      alert('No specifications need a charmed price update.');
      return;
    }

    if (!window.confirm(`Update Sell Price to Suggested Charm for all ${specsToUpdate.length} specifications?`)) {
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      const token = await currentUser.getIdToken();

      const updatePromises = specsToUpdate.map(async ({ spec, charmedPrice }) => {
        const id = String(spec.id || '').trim();
        const payload = buildSpecUpdatePayload(spec, { sellPrice: String(charmedPrice) });
        const response = await fetch(`/api/v1/fabric-specifications/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || `Failed to update spec ${spec.fabricNameForOffice || id}`);
        }
      });

      await Promise.all(updatePromises);
      await fetchSpecs();
      alert(`Successfully updated all ${specsToUpdate.length} specifications!`);
    } catch (err) {
      setError(err.message || 'Failed to update some specifications.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fabric-specs-container">
      <div className="page-header">
        <h1>Fabric Specifications</h1>
        <div className="header-actions">
          {canPerformAction('fabricSpecs.import') && (
            <>
              <label htmlFor="csv-import-input" className="action-button import-btn">
                Import CSV
              </label>
              <input id="csv-import-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={() => {}} disabled={isSubmitting} />
            </>
          )}
          <button onClick={() => {}} className="action-button export-btn">Export CSV</button>
        </div>
      </div>

      {(canPerformAction('fabricSpecs.create') || canPerformAction('fabricSpecs.edit')) && (
        <div className="specs-form-section">
          <h2>{editingId ? 'Edit Specification' : 'Add New Specification'}</h2>
          <form onSubmit={handleSubmit} className="specs-form">
            <div className="form-grid">
              {/* Form fields */}
              <div className="form-group">
                <label>Type</label>
                <input name="type" value={formState.type} onChange={handleInputChange} list="type-options" autoComplete="off" />
                <datalist id="type-options">
                  {uniqueValues.type.map(val => <option key={val} value={val} />)}
                </datalist>
              </div>
              <div className="form-group">
                <label>Quality</label>
                <input name="quality" value={formState.quality} onChange={handleInputChange} list="quality-options" autoComplete="off" />
                <datalist id="quality-options">
                  {uniqueValues.quality.map(val => <option key={val} value={val} />)}
                </datalist>
              </div>
              <div className="form-group">
                <label>Fabric Name (Office)</label>
                <input name="fabricNameForOffice" value={formState.fabricNameForOffice} onChange={handleInputChange} list="fabricName-options" autoComplete="off" />
                <datalist id="fabricName-options">
                  {uniqueValues.fabricNameForOffice.map(val => <option key={val} value={val} />)}
                </datalist>
              </div>
              <div className="form-group">
                <label>Fabric</label>
                <input name="fabric" value={formState.fabric} onChange={handleInputChange} list="fabric-options" autoComplete="off" />
                <datalist id="fabric-options">
                  {uniqueValues.fabric.map(val => <option key={val} value={val} />)}
                </datalist>
              </div>
              <div className="form-group">
                <label>SKU Prefix*</label>
                <input name="skuPrefix" value={formState.skuPrefix} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>SKU Sub Prefix</label>
                <input name="skuSubPrefix" value={formState.skuSubPrefix} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>SKU Suffix</label>
                <input name="skuSuffix" value={formState.skuSuffix} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Sell Price</label>
                <input type="number" name="sellPrice" value={formState.sellPrice} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Purchase Price</label>
                <input type="number" name="purchasePrice" value={formState.purchasePrice} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Width</label>
                <input name="width" value={formState.width} onChange={handleInputChange} list="width-options" autoComplete="off" />
                <datalist id="width-options">
                  {uniqueValues.width.map(val => <option key={val} value={val} />)}
                </datalist>
              </div>
              <div className="form-group">
                <label>Weight Mode</label>
                <select name="weightMode" value={formState.weightMode} onChange={handleInputChange}>
                  <option value="fixed">Fixed</option>
                  <option value="dynamic">Dynamic</option>
                </select>
              </div>
              <div className="form-group">
                <label>Weight (grams)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  name="weightInGrams"
                  value={formState.weightInGrams}
                  onChange={handleInputChange}
                  disabled={
                    formState.weightMode === 'dynamic' ||
                    (Boolean(editingId) && String(formState.weightInGrams || '').trim() !== '')
                  }
                  placeholder={formState.weightMode === 'dynamic' ? 'Dynamic by apparel' : 'e.g. 350'}
                />
              </div>
              <div className="form-group">
                <label>GSM</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  name="gsm"
                  value={formState.gsm}
                  onChange={handleInputChange}
                  placeholder="e.g. 95"
                />
              </div>
              <div className="form-group">
                <label>Batch Quality*</label>
                <select name="batchQuality" value={formState.batchQuality} onChange={handleInputChange} required>
                  <option value="Polyester">Polyester</option>
                  <option value="Pure">Pure</option>
                  <option value="Plain">Plain</option>
                  <option value="Modal">Modal</option>
                  <option value="Cotton">Cotton</option>
                </select>
              </div>
              <div className="form-group">
                <label>Batch Prefix</label>
                <input name="batchPrefix" value={formState.batchPrefix} readOnly />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="printQualityEnabled"
                    checked={formState.printQualityEnabled}
                    onChange={handleInputChange}
                  />
                  {' '}Print Quality (show on "print this design on another fabric" picker)
                </label>
              </div>
              <div className="form-group">
                <label>Print Quality Display Name</label>
                <input
                  name="printQualityDisplayName"
                  value={formState.printQualityDisplayName}
                  onChange={handleInputChange}
                  placeholder="Blank = use Fabric name as-is. Only needed when two rows share a Fabric name."
                  disabled={!formState.printQualityEnabled}
                />
              </div>
            </div>
            <div className="form-actions">
              {editingId && <button type="button" onClick={handleCancelEdit} className="cancel-btn">Cancel</button>}
              <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : (editingId ? 'Update' : 'Add Specification')}</button>
            </div>
          </form>
        </div>
      )}

      <div className="specs-table-section">
        <h2>Missing Specifications</h2>
        {isFetchingMissing && <p>Loading missing specs...</p>}
        {!isFetchingMissing && missingSpecs.length > 0 ? (
          <div className="missing-specs-list">
            <p>The following SKUs were found in orders but do not match any existing fabric specification. Click "Add Spec" to create a new rule for them.</p>
            <ul>
              {missingSpecs.map(sku => (
                <li key={sku}>
                  <span>{sku}</span>
                  <button onClick={() => {}} className="add-spec-btn">Add Spec</button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          !isFetchingMissing && <p>No missing specifications found. All SKUs in orders are covered.</p>
        )}
        <button onClick={fetchMissingSpecs} disabled={isFetchingMissing} className="refresh-btn">{isFetchingMissing ? 'Refreshing...' : 'Refresh Missing Specs'}</button>
      </div>

      {canPerformAction('fabricSpecs.edit') && (
        <div className="specs-table-section">
          <button type="button" onClick={handleSyncPrintQualities} disabled={isSyncing}>
            {isSyncing ? 'Syncing…' : 'Sync Print Qualities to Shopify'}
          </button>
          <span>
            {' '}Writes checked rows into the storefront's Print Quality picker. Also runs automatically every night at 00:15 IST.
          </span>
          {syncStatus?.lastRunAt && (
            <p>
              Last run: {new Date(syncStatus.lastRunAt).toLocaleString()}
              {syncStatus.lastSkipped ? ' (skipped — nothing checked in)' : ` — ${syncStatus.lastQualitiesCount} qualities`}
            </p>
          )}
          {syncMessage && <p>{syncMessage}</p>}
        </div>
      )}

      {error && <p className="error-message">{error}</p>}

      <div className="table-wrapper">
        {isLoading && <div className="loading-overlay"><p>Loading Specs...</p></div>}
        <table className={`specs-table ${isLoading ? 'loading' : ''}`}>
          <thead>
            <tr>
              <th>No</th>
              <th>
                <button type="button" className="table-sort-btn" onClick={() => toggleSort('type')}>Type</button>
              </th>
              <th>
                <button type="button" className="table-sort-btn" onClick={() => toggleSort('quality')}>Quality</button>
              </th>
              <th>
                <button type="button" className="table-sort-btn" onClick={() => toggleSort('skuPrefix')}>SKU Prefix</button>
              </th>
              <th>
                <button type="button" className="table-sort-btn" onClick={() => toggleSort('skuSubPrefix')}>SKU Sub Prefix</button>
              </th>
              <th>
                <button type="button" className="table-sort-btn" onClick={() => toggleSort('skuSuffix')}>SKU Suffix</button>
              </th>
              <th>Batch Quality</th>
              <th>Batch Prefix</th>
              <th>
                <button type="button" className="table-sort-btn" onClick={() => toggleSort('fabricNameForOffice')}>Fabric Name (Office)</button>
              </th>
              <th>
                <button type="button" className="table-sort-btn" onClick={() => toggleSort('fabric')}>Fabric</button>
              </th>
              <th>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                  <span>Suggested Charm</span>
                  {canPerformAction('fabricSpecs.edit') && (
                    <button
                      type="button"
                      className="action-button edit-btn"
                      style={{ fontSize: '0.75rem', padding: '2px 6px', height: 'auto' }}
                      title="Apply Suggested Charm to Sell Price for all matching rows"
                      onClick={handleApplyAllSuggestedCharm}
                    >
                      Apply All
                    </button>
                  )}
                </div>
              </th>
              <th>Sell Price</th>
              <th>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                  <span>Suggested Price</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <input
                      type="number"
                      min="0"
                      max="99"
                      step="0.1"
                      value={discountPct}
                      onChange={(e) => setDiscountPct(e.target.value)}
                      placeholder="disc"
                      style={{ width: '60px' }}
                    />
                    <span>%</span>
                  </div>
                </div>
              </th>
              <th>Purchase Price</th>
              <th>Width</th>
              <th>Weight Mode</th>
              <th>Weight (g)</th>
              <th>GSM</th>
              <th>Print Quality</th>
              <th>Print Quality Name</th>
              <th>Actions</th>
            </tr>
            <tr>
              <th></th>
              <th><input value={tableFilters.type} onChange={(e) => setTableFilters((p) => ({ ...p, type: e.target.value }))} placeholder="Filter" /></th>
              <th><input value={tableFilters.quality} onChange={(e) => setTableFilters((p) => ({ ...p, quality: e.target.value }))} placeholder="Filter" /></th>
              <th><input value={tableFilters.skuPrefix} onChange={(e) => setTableFilters((p) => ({ ...p, skuPrefix: e.target.value }))} placeholder="Filter" /></th>
              <th><input value={tableFilters.skuSubPrefix} onChange={(e) => setTableFilters((p) => ({ ...p, skuSubPrefix: e.target.value }))} placeholder="Filter" /></th>
              <th><input value={tableFilters.skuSuffix} onChange={(e) => setTableFilters((p) => ({ ...p, skuSuffix: e.target.value }))} placeholder="Filter" /></th>
              <th></th>
              <th></th>
              <th><input value={tableFilters.fabricNameForOffice} onChange={(e) => setTableFilters((p) => ({ ...p, fabricNameForOffice: e.target.value }))} placeholder="Filter" /></th>
              <th><input value={tableFilters.fabric} onChange={(e) => setTableFilters((p) => ({ ...p, fabric: e.target.value }))} placeholder="Filter" /></th>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {displayedSpecs.map(spec => (
              <tr key={spec.id}>
                <td>{displayedSpecs.indexOf(spec) + 1}</td>
                <td>{spec.type}</td>
                <td>{spec.quality}</td>
                <td>{spec.skuPrefix}</td>
                <td>{spec.skuSubPrefix}</td>
                <td>{spec.skuSuffix}</td>
                <td>{spec.batchQuality}</td>
                <td>{spec.batchPrefix}</td>
                <td>{spec.fabricNameForOffice}</td>
                <td>{spec.fabric}</td>
                <td>
                  {(() => {
                    const suggested = computeSuggestedPrice(spec.sellPrice);
                    const targetPrice = suggested !== '' ? suggested : spec.sellPrice;
                    if (!targetPrice) return null;
                    const charmed = charmNearestEnding(targetPrice);
                    if (!charmed || Math.abs(Number(charmed) - Number(spec.sellPrice)) < 0.01) return null;
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        <span style={{ fontWeight: '500', color: '#2e7d32' }}>{charmed}</span>
                        {canPerformAction('fabricSpecs.edit') && (
                          <button
                            type="button"
                            className="action-button edit-btn"
                            title="Apply this charmed price to Sell Price"
                            onClick={() => handleApplySuggestedCharm(spec)}
                            disabled={rowSellPriceSavingId === spec.id}
                          >
                            ✓
                          </button>
                        )}
                      </span>
                    );
                  })()}
                </td>
                <td className={`sell-price-cell${isSellPriceRecent(spec) ? ' recent' : ''}`}>
                  {editingSellPriceId === spec.id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        autoFocus
                        value={String(rowSellPriceDrafts[spec.id] ?? '')}
                        onChange={(e) => setRowSellPriceDrafts((prev) => ({ ...prev, [spec.id]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveSellPrice(spec, rowSellPriceDrafts[spec.id]);
                          if (e.key === 'Escape') handleSellPriceEditCancel(spec.id);
                        }}
                        style={{ width: '80px' }}
                      />
                      <button type="button" className="action-button edit-btn" onClick={() => saveSellPrice(spec, rowSellPriceDrafts[spec.id])} disabled={rowSellPriceSavingId === spec.id}>
                        {rowSellPriceSavingId === spec.id ? '...' : '✓'}
                      </button>
                      <button type="button" className="action-button" onClick={() => handleSellPriceEditCancel(spec.id)}>✕</button>
                    </div>
                  ) : (
                    <>
                      <span className="sell-price-value">
                        {spec.sellPrice}
                        {isSellPriceRecent(spec) && (
                          <span className="recent-badge" title={`Sell price updated ${sellPriceDaysAgo(spec)}`}>updated</span>
                        )}
                        {canPerformAction('fabricSpecs.edit') && (
                          <button type="button" className="inline-edit-icon" title="Edit sell price" onClick={() => handleSellPriceEditStart(spec)}>✎</button>
                        )}
                      </span>
                      {spec.sellPricePrev != null && spec.sellPricePrev !== '' && spec.sellPriceUpdatedAtMs && (
                        <div className="sell-price-history">
                          from {spec.sellPricePrev} to {spec.sellPrice} on {formatSellPriceDate(spec.sellPriceUpdatedAtMs)}
                        </div>
                      )}
                    </>
                  )}
                </td>
                <td>
                  {(() => {
                    const suggested = computeSuggestedPrice(spec.sellPrice);
                    if (suggested === '') return null;
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        {suggested}
                        {canPerformAction('fabricSpecs.edit') && (
                          <button
                            type="button"
                            className="action-button edit-btn"
                            title="Apply this price to Sell Price"
                            onClick={() => handleApplySuggested(spec)}
                            disabled={rowSellPriceSavingId === spec.id}
                          >
                            ✓
                          </button>
                        )}
                      </span>
                    );
                  })()}
                </td>
                <td>{spec.purchasePrice}</td>
                <td>{spec.width}</td>
                <td>{(spec.weightMode || 'fixed')}</td>
                <td>
                  {String(spec.weightMode || 'fixed').toLowerCase() === 'dynamic' ? (
                    'Dynamic'
                  ) : (spec.weightInGrams == null || spec.weightInGrams === '') ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={String(rowWeightDrafts[spec.id] ?? '')}
                        onChange={(e) => setRowWeightDrafts((prev) => ({ ...prev, [spec.id]: e.target.value }))}
                        placeholder="grams"
                        style={{ width: '90px' }}
                      />
                      <button
                        type="button"
                        className="action-button edit-btn"
                        onClick={() => handleInlineWeightSave(spec)}
                        disabled={rowWeightSavingId === spec.id}
                      >
                        {rowWeightSavingId === spec.id ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  ) : (
                    spec.weightInGrams
                  )}
                </td>
                <td>
                  {editingGsmId === spec.id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        autoFocus
                        value={String(rowGsmDrafts[spec.id] ?? '')}
                        onChange={(e) => setRowGsmDrafts((prev) => ({ ...prev, [spec.id]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveGsm(spec, rowGsmDrafts[spec.id]);
                          if (e.key === 'Escape') handleGsmEditCancel(spec.id);
                        }}
                        style={{ width: '70px' }}
                      />
                      <button type="button" className="action-button edit-btn" onClick={() => saveGsm(spec, rowGsmDrafts[spec.id])} disabled={rowGsmSavingId === spec.id}>
                        {rowGsmSavingId === spec.id ? '...' : '✓'}
                      </button>
                      <button type="button" className="action-button" onClick={() => handleGsmEditCancel(spec.id)}>✕</button>
                    </div>
                  ) : (
                    <span>
                      {spec.gsm != null && spec.gsm !== '' ? spec.gsm : <span style={{ color: '#aaa' }}>—</span>}
                      {canPerformAction('fabricSpecs.edit') && (
                        <button type="button" className="inline-edit-icon" title="Edit GSM" onClick={() => handleGsmEditStart(spec)}>✎</button>
                      )}
                    </span>
                  )}
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={spec.printQualityEnabled === true}
                    disabled={rowPrintQualitySavingId === spec.id || !canPerformAction('fabricSpecs.edit')}
                    onChange={() => handleTogglePrintQuality(spec)}
                  />
                </td>
                <td>
                  {editingPrintQualityNameId === spec.id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <input
                        type="text"
                        autoFocus
                        value={String(rowPrintQualityNameDrafts[spec.id] ?? '')}
                        onChange={(e) => setRowPrintQualityNameDrafts((prev) => ({ ...prev, [spec.id]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') savePrintQualityName(spec, rowPrintQualityNameDrafts[spec.id]);
                          if (e.key === 'Escape') handlePrintQualityNameEditCancel(spec.id);
                        }}
                        placeholder="Blank = use Fabric name"
                        style={{ width: '160px' }}
                      />
                      <button type="button" className="action-button edit-btn" onClick={() => savePrintQualityName(spec, rowPrintQualityNameDrafts[spec.id])} disabled={rowPrintQualitySavingId === spec.id}>
                        {rowPrintQualitySavingId === spec.id ? '...' : '✓'}
                      </button>
                      <button type="button" className="action-button" onClick={() => handlePrintQualityNameEditCancel(spec.id)}>✕</button>
                    </div>
                  ) : (
                    <span>
                      {spec.printQualityEnabled ? (spec.printQualityDisplayName || spec.fabric) : ''}
                      {spec.printQualityEnabled && canPerformAction('fabricSpecs.edit') && (
                        <button type="button" className="inline-edit-icon" title="Edit Print Quality Name" onClick={() => handlePrintQualityNameEditStart(spec)}>✎</button>
                      )}
                    </span>
                  )}
                </td>
                <td className="actions-cell">
                  {canPerformAction('fabricSpecs.edit') && (
                    <button onClick={() => handleEdit(spec)} className="action-button edit-btn">Edit</button>
                  )}
                  {canPerformAction('fabricSpecs.delete') && (
                    <button onClick={() => handleDelete(spec.id)} className="action-button delete-btn">Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default FabricSpecificationPage;
